import { Fragment } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { tokens } from "src/locales/tokens";
import {
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Paper,
  Stack,
  SvgIcon,
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
  Skeleton,
} from "@mui/material";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ZoomInMapIcon from "@mui/icons-material/ZoomInMap";
import { usePopover } from "src/hooks/use-popover";
import { PopoverCompareTime } from "./popover-compare-time";

const ReadingRow = (props) => {
  const { reading, onZoomToMap, ...other } = props;

  const getMainColor = (status) => {
    if (status === "critical") {
      return "error";
    }

    if (status === "warning") {
      return "warning.main";
    }

    return "inherit";
  };

  const getDiffColor = (diff) => {
    if (diff > 0) {
      return "success.main";
    }

    if (diff < 0) {
      return "error";
    }

    return "inherit";
  };

  const read = reading.read ? parseFloat(reading.read) : 0;
  const mainColor = getMainColor(reading.status);
  const diff = reading.prevRead
    ? read - (reading.prevRead ? parseFloat(reading.prevRead) : 0)
    : "-";
  const diffColor = getDiffColor(diff);

  return (
    <Fragment>
      <TableRow {...other}>
        <TableCell
          sx={{
            py: 1,
            px: 3,
            m: 1,
          }}
          align={"left"}
        >
          <Stack
            direction={"row"}
            alignItems={"center"}
            spacing={1}
          >
            <Tooltip title={"Zoom In"}>
              <IconButton
                size="small"
                onClick={() =>
                  onZoomToMap({
                    id: reading.stationId,
                    coordX: reading.coordX,
                    coordY: reading.coordY,
                  })
                }
              >
                <SvgIcon>
                  <ZoomInMapIcon />
                </SvgIcon>
              </IconButton>
            </Tooltip>
            <Stack direction={"column"}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontSize: "0.95rem",
                  lineHeight: 1.2,
                }}
              >
                {reading.stationName}
              </Typography>
              <Typography
                variant="subtitle2"
                color="text.secondary"
              >
                {`Tarikh bacaan: ${reading.readDateTime || "N/A"}`}
              </Typography>
            </Stack>
          </Stack>
        </TableCell>
        <TableCell
          sx={{
            px: 3,
            py: 1,
          }}
          align={"right"}
        >
          <Typography
            color={mainColor}
            variant="subtitle1"
            sx={{
              fontSize: "0.95rem",
            }}
          >
            {read ? read.toFixed(1) : "--"}
          </Typography>
          <Typography
            color={diffColor}
            variant="subtitle2"
            sx={{
              lineHeight: 1.2,
            }}
          >
            {diff !== "-"
              ? diff === 0
                ? "-"
                : diff > 0
                ? "+" + diff.toFixed(1)
                : diff.toFixed(1)
              : diff}
          </Typography>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

export const OverviewPublicTable = (props) => {
  const {
    tableData = [],
    currentCompareTime = "15 MINUTES",
    onCompareTimeChange = () => {},
    onZoomToMap = () => {},
    isLoading,
  } = props;
  const { t } = useTranslation();
  const popover = usePopover();

  const handleCompareTimeChange = (time) => {
    onCompareTimeChange?.(time);
    popover.handleToggle?.();
  };

  const handleZoom = async (station) => {
    onZoomToMap?.(station);
  };

  return (
    <Card>
      <CardHeader
        title={"Indeks Kualiti Air"}
        sx={{
          pt: 2,
          pb: 0,
        }}
        action={
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              py: 1,
            }}
            spacing={1}
          >
            <Tooltip title={t(tokens.label.compareTime)}>
              <IconButton
                size="small"
                onClick={popover.handleOpen}
                ref={popover.anchorRef}
              >
                <SvgIcon>
                  <ScheduleOutlinedIcon />
                </SvgIcon>
              </IconButton>
            </Tooltip>
            <PopoverCompareTime
              anchorEl={popover.anchorRef.current}
              onClose={popover.handleClose}
              open={popover.open}
              currentCompareTime={currentCompareTime}
              onCompareTimeChange={handleCompareTimeChange}
            />
          </Stack>
        }
      />
      <CardContent sx={{ pt: 2, px: 0, pb: "0px !important" }}>
        <Paper>
          <TableContainer
            sx={{
              marginTop: 0,
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    align="left"
                    sx={{
                      background: "white",
                      px: 3,
                      py: 2,
                      m: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        textTransform: "none",
                        lineHeight: 1.0,
                      }}
                      variant="button"
                    >
                      Stesen
                    </Typography>
                  </TableCell>
                  <TableCell
                    align={"right"}
                    sx={{
                      px: 3,
                      background: "white",
                      py: 0,
                      m: 0,
                    }}
                    width={"17%"}
                  >
                    <Typography
                      sx={{
                        textTransform: "none",
                        lineHeight: 1.0,
                      }}
                      variant="button"
                    >
                      Indeks Kualiti Air
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              {isLoading ? (
                <TableBody>
                  {Array.from({ length: 3 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton
                          variant="text"
                          width="100%"
                        />
                      </TableCell>
                      <TableCell key={index}>
                        <Skeleton
                          variant="text"
                          width="100%"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              ) : tableData.length > 0 ? (
                <TableBody>
                  {tableData.map((reading, index) => (
                    <ReadingRow
                      key={index}
                      reading={reading}
                      onZoomToMap={handleZoom}
                    />
                  ))}
                </TableBody>
              ) : (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Stack textAlign="center">
                        <Typography
                          color="text.primary"
                          variant="subtitle2"
                        >
                          {t(tokens.label.noDataFound)}
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Paper>
      </CardContent>
    </Card>
  );
};

OverviewPublicTable.propTypes = {
  tableData: PropTypes.array,
  lastDataRefreshed: PropTypes.string,
  currentCompareTime: PropTypes.string,
  onCompareTimeChange: PropTypes.func,
  onZoomToMap: PropTypes.func,
  isLoading: PropTypes.bool,
};
